import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, FileText, Megaphone, MapPin, AlertTriangle, Search, Phone,
  Calendar, Users, Hammer, CheckCircle2, FileCheck, CalendarCheck, ChevronRight,
  ShieldCheck, Sparkles, Building2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  BARANGAY, BRAND, CAPTAIN, ANNOUNCEMENTS, PROGRAMS, ACCOMPLISHMENT_METRICS,
} from '@/data/mockData';
import { formatDate } from '@/lib/storage';
import { SectionHeader } from '@/components/shared/SectionHeader';

const QUICK_ACTIONS = [
  { to: '/request-document', icon: FileText, title: 'Request a Document', desc: 'Clearance, indigency, residency & more.', color: 'from-primary to-primary-glow' },
  { to: '/report-concern', icon: Megaphone, title: 'Report a Concern', desc: 'Streetlights, drainage, garbage, safety.', color: 'from-accent to-cyan-400' },
  { to: '/track-request', icon: Search, title: 'Track Request', desc: 'Check the status of your transaction.', color: 'from-violet-500 to-fuchsia-500' },
  { to: '/emergency', icon: Phone, title: 'Emergency Contacts', desc: 'Tanod, BFP, PNP, ambulance hotlines.', color: 'from-rose-500 to-orange-500' },
];

const METRIC_ICONS = { Users, FileCheck, Megaphone, CheckCircle2, Hammer, CalendarCheck };

const Home = () => {
  const pinned = ANNOUNCEMENTS.filter((a) => a.pinned).slice(0, 2);
  const latest = ANNOUNCEMENTS.filter((a) => !a.pinned).slice(0, 3);
  const upcomingPrograms = PROGRAMS.slice(0, 3);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        className="relative isolate overflow-hidden bg-gradient-hero bg-cover bg-center text-primary-foreground"
        style={{
          backgroundImage:
            "linear-gradient(135deg, hsl(224 78% 20% / 0.82) 0%, hsl(224 76% 30% / 0.7) 45%, hsl(210 82% 34% / 0.7) 100%), url('/hero-bg.png')",
        }}
      >
        <div className="absolute inset-0 bg-grid-pattern opacity-10" />
        <div className="absolute -top-40 -right-32 h-96 w-96 rounded-full bg-accent/30 blur-3xl" />
        <div className="absolute -bottom-32 -left-20 h-96 w-96 rounded-full bg-primary-glow/30 blur-3xl" />
        <div className="container-app relative pt-16 pb-20 md:pt-24 md:pb-28 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <Badge className="bg-white/15 hover:bg-white/20 border-white/20 text-primary-foreground backdrop-blur" data-testid="hero-eyebrow">
              <Sparkles className="h-3 w-3 mr-1.5" /> Smart Barangay Action Center
            </Badge>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-balance">
              Welcome to <span className="text-accent">{BARANGAY.name}</span>
            </h1>
            <p className="mt-4 text-base sm:text-lg font-semibold uppercase tracking-[0.16em] text-accent/95">
              Official Barangay Action Center
            </p>
            <p className="mt-5 text-lg/relaxed opacity-90 max-w-xl">
              Access barangay services, updates, programs, and emergency contacts in one place.
            </p>
            <p className="mt-3 text-sm font-medium uppercase tracking-[0.18em] opacity-80">
              {BARANGAY.tagline}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-accent-glow" data-testid="hero-request-cta">
                <Link to="/request-document">
                  Request a Document <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-white/5 border-white/30 text-primary-foreground hover:bg-white/15 hover:text-primary-foreground" data-testid="hero-concern-cta">
                <Link to="/report-concern">Report a Concern</Link>
              </Button>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              <Stat label="Population" value={BARANGAY.population} />
              <Stat label="Households" value={BARANGAY.households} />
              <Stat label="Puroks" value={BARANGAY.puroks} />
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-6 bg-gradient-to-br from-white/20 to-transparent rounded-3xl blur-2xl" />
            <Card className="relative overflow-hidden bg-white text-foreground shadow-elegant border-0 rounded-3xl">
              <div className="aspect-[16/9] overflow-hidden bg-secondary">
                <img
                  src={BRAND.communityHeroUrl}
                  alt={`${BARANGAY.name} community`}
                  className="h-full w-full object-cover"
                />
              </div>
              <CardContent className="p-6">
                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-primary">Public Service Portal</div>
                <h3 className="mt-2 font-display text-xl font-bold text-primary-deep">For Every Resident of {BARANGAY.name}</h3>
                <p className="mt-3 text-sm text-foreground/80 leading-relaxed">
                  This platform is focused on faster transactions, transparent updates, and easier access to barangay support.
                </p>
                <ul className="mt-4 space-y-2 text-sm text-foreground/85">
                  <li className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> Document requests and status tracking</li>
                  <li className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> Concern reporting and follow-ups</li>
                  <li className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> Programs, advisories, and emergency contacts</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* QUICK ACTIONS */}
      <section className="container-app -mt-12 md:-mt-16 relative z-10" data-testid="quick-actions">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {QUICK_ACTIONS.map((qa) => (
            <Link
              key={qa.to}
              to={qa.to}
              className="group relative overflow-hidden rounded-2xl bg-card border shadow-md-soft p-5 hover:shadow-lg-soft hover:-translate-y-1 transition-all duration-300"
              data-testid={`qa-${qa.to.replace('/', '')}`}
            >
              <div className={`absolute -right-8 -top-8 h-24 w-24 rounded-full bg-gradient-to-br ${qa.color} opacity-15 group-hover:opacity-30 transition-opacity`} />
              <qa.icon className="h-6 w-6 text-primary" strokeWidth={2.2} />
              <div className="mt-4 font-heading font-semibold text-sm sm:text-base">{qa.title}</div>
              <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{qa.desc}</div>
              <ArrowRight className="absolute bottom-4 right-4 h-4 w-4 text-primary opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
            </Link>
          ))}
        </div>
      </section>

      {/* ANNOUNCEMENTS */}
      <section className="container-app section-padding">
        <div className="flex items-end justify-between mb-8">
          <SectionHeader
            eyebrow="Latest Updates"
            title="Announcements & Advisories"
            description="Stay informed about programs, advisories and important community news."
            className="mb-0"
          />
          <Button asChild variant="ghost" className="hidden sm:inline-flex" data-testid="see-all-announcements">
            <Link to="/announcements">See all <ArrowRight className="ml-1 h-4 w-4" /></Link>
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          {pinned.map((a) => (
            <AnnouncementCard key={a.id} a={a} pinned />
          ))}
          {latest.map((a) => (
            <AnnouncementCard key={a.id} a={a} />
          ))}
        </div>
      </section>

      {/* PROGRAMS */}
      <section className="bg-secondary/40">
        <div className="container-app section-padding">
          <SectionHeader
            eyebrow="Upcoming"
            title="Programs & Events"
            description="Free community programs from health missions to youth leadership and livelihood training."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {upcomingPrograms.map((p) => (
              <Card key={p.id} className="overflow-hidden card-elevated bg-card border-0">
                <div className="aspect-[16/10] overflow-hidden bg-secondary">
                  <img src={p.image} alt={p.title} className="h-full w-full object-cover hover:scale-105 transition duration-700" />
                </div>
                <CardContent className="p-5">
                  <Badge variant="secondary" className="bg-primary/10 text-primary hover:bg-primary/20">{p.category}</Badge>
                  <h3 className="mt-3 font-heading font-semibold leading-snug">{p.title}</h3>
                  <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{formatDate(p.date)}</span>
                    <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{p.location}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Button asChild className="bg-gradient-primary text-primary-foreground" data-testid="see-all-programs">
              <Link to="/programs">View all programs <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* METRICS */}
      <section className="container-app section-padding">
        <SectionHeader
          eyebrow="By the Numbers"
          title="Our Accomplishments"
          description="A snapshot of what the barangay has delivered for the community this year."
        />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {ACCOMPLISHMENT_METRICS.map((m) => {
            const Icon = METRIC_ICONS[m.icon] || Users;
            return (
              <Card key={m.label} className="p-5 card-elevated text-center border-0">
                <div className="mx-auto inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-primary text-primary-foreground shadow-md-soft">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="mt-3 font-display text-2xl font-bold gradient-text">{m.value.toLocaleString()}+</div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{m.label}</div>
              </Card>
            );
          })}
        </div>
        <div className="mt-8 text-center">
          <Button asChild variant="outline" data-testid="see-accomplishments">
            <Link to="/accomplishments">View detailed accomplishments <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
      </section>

      {/* PRIORITIES */}
      <section className="container-app section-padding">
        <SectionHeader
          eyebrow="Service Priorities"
          title="Priorities for San Isidro"
          description="Seven pillars that guide every program, project, and decision."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {CAPTAIN.priorities.slice(0, 6).map((p, i) => (
            <Card key={i} className="p-5 card-elevated border-0">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-accent-soft text-accent">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <h4 className="mt-3 font-heading font-semibold">{p.title}</h4>
              <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* EMERGENCY CTA */}
      <section className="container-app pb-20">
        <Card className="overflow-hidden bg-gradient-to-br from-rose-600 via-orange-500 to-amber-500 text-white border-0 shadow-elegant">
          <div className="grid lg:grid-cols-3 gap-6 p-8 lg:p-12">
            <div className="lg:col-span-2">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] backdrop-blur">
                <AlertTriangle className="h-3.5 w-3.5" /> Emergency Contacts
              </div>
              <h3 className="mt-3 font-display text-2xl sm:text-3xl font-bold">In an emergency, every second counts.</h3>
              <p className="mt-2 opacity-90 max-w-xl">
                Save these numbers on your phone — barangay tanod, BFP, PNP, BHW, and ambulance lines for {BARANGAY.name}.
              </p>
              <Button asChild size="lg" className="mt-6 bg-white text-rose-600 hover:bg-white/90" data-testid="emergency-cta">
                <Link to="/emergency"><Phone className="mr-2 h-4 w-4" /> View all hotlines</Link>
              </Button>
            </div>
            <div className="grid gap-3 self-center">
              <EmergencyChip label="Barangay Hotline" number={BARANGAY.hotline} />
              <EmergencyChip label="National Hotline" number="911" />
              <EmergencyChip label="BFP" number="(02) 8655-0222" />
            </div>
          </div>
        </Card>
      </section>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <div className="font-display text-2xl font-bold">{value}</div>
    <div className="text-[11px] uppercase tracking-widest opacity-75">{label}</div>
  </div>
);

const AnnouncementCard = ({ a, pinned }) => (
  <Link to={`/announcements/${a.id}`} className="group block" data-testid={`home-announcement-${a.id}`}>
    <Card className="overflow-hidden card-elevated border-0 h-full">
      {a.image ? (
        <div className="aspect-[16/9] overflow-hidden bg-secondary">
          <img src={a.image} alt={a.title} className="h-full w-full object-cover group-hover:scale-105 transition duration-700" />
        </div>
      ) : (
        <div className="aspect-[16/9] bg-gradient-to-br from-primary/15 to-accent-soft flex items-center justify-center">
          <Building2 className="h-10 w-10 text-primary/40" />
        </div>
      )}
      <CardContent className="p-5">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="secondary" className="bg-primary/10 text-primary hover:bg-primary/20">{a.category}</Badge>
          {pinned && <Badge className="bg-warning/15 text-warning hover:bg-warning/25 border-warning/30">Pinned</Badge>}
          <span className="text-[11px] text-muted-foreground">{formatDate(a.date)}</span>
        </div>
        <h3 className="mt-3 font-heading font-semibold leading-snug line-clamp-2">{a.title}</h3>
        <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{a.summary}</p>
        <div className="mt-3 inline-flex items-center text-xs font-semibold text-primary group-hover:gap-1.5 gap-1 transition-all">
          Read more <ChevronRight className="h-3.5 w-3.5" />
        </div>
      </CardContent>
    </Card>
  </Link>
);

const EmergencyChip = ({ label, number }) => (
  <div className="rounded-xl bg-white/15 backdrop-blur px-4 py-3 flex items-center justify-between">
    <div>
      <div className="text-[11px] uppercase tracking-widest opacity-90">{label}</div>
      <div className="font-display text-lg font-bold">{number}</div>
    </div>
    <Phone className="h-5 w-5 opacity-90" />
  </div>
);

export default Home;
